import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';

interface QueueMonitoringFixedProps {
  apiKey: string;
}

interface QueueData {
  health: any;
  metrics: any[];
  backpressure: any;
  deadLetterQueue: any;
  lastUpdated: Date;
}

export const QueueMonitoringFixed: React.FC<QueueMonitoringFixedProps> = ({ apiKey }) => {
  // Simple state - no complex objects that could cause re-render loops
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<QueueData | null>(null);
  const [timeRange, setTimeRange] = useState<'5m' | '15m' | '1h' | '6h'>('15m');
  
  // Use refs to prevent infinite loops
  const isMountedRef = useRef(true);
  const fetchCountRef = useRef(0);
  const lastFetchTimeRef = useRef(0);

  // Logging with frequency limits to prevent console spam
  const logWithLimit = (message: string, level: 'log' | 'warn' | 'error' = 'log') => {
    const now = Date.now();
    if (now - lastFetchTimeRef.current > 1000) { // Max 1 log per second
      console[level](`[QueueFixed] ${message}`);
      lastFetchTimeRef.current = now;
    }
  };

  // Manual data fetching function - no useCallback to avoid dependency issues
  const fetchData = async (force = false) => {
    fetchCountRef.current++;
    const fetchId = fetchCountRef.current;
    
    logWithLimit(`Fetch #${fetchId} started (force: ${force})`);
    
    // Prevent too frequent fetches
    const now = Date.now();
    if (!force && data && (now - data.lastUpdated.getTime()) < 5000) {
      logWithLimit('Skipping fetch - too recent');
      return;
    }

    if (loading && !force) {
      logWithLimit('Skipping fetch - already loading');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      };

      // Sequential fetching to avoid race conditions
      const fetchWithTimeout = async (url: string, timeout = 5000) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        
        try {
          const response = await fetch(url, { 
            headers, 
            signal: controller.signal 
          });
          clearTimeout(timeoutId);
          return response;
        } catch (error) {
          clearTimeout(timeoutId);
          throw error;
        }
      };

      logWithLimit('Fetching health data...');
      const healthResponse = await fetchWithTimeout('/api/v1/queue/health');
      const healthData = healthResponse.ok ? await healthResponse.json() : null;

      logWithLimit('Fetching metrics data...');
      const metricsResponse = await fetchWithTimeout(`/api/v1/queue/metrics?range=${timeRange}`);
      const metricsData = metricsResponse.ok ? await metricsResponse.json() : null;

      logWithLimit('Fetching backpressure data...');
      const backpressureResponse = await fetchWithTimeout('/api/v1/queue/backpressure');
      const backpressureData = backpressureResponse.ok ? await backpressureResponse.json() : null;

      logWithLimit('Fetching DLQ data...');
      const dlqResponse = await fetchWithTimeout('/api/v1/queue/dead-letter');
      const dlqData = dlqResponse.ok ? await dlqResponse.json() : null;

      // Only update state if component is still mounted and this is the latest fetch
      if (isMountedRef.current && fetchId === fetchCountRef.current) {
        const newData: QueueData = {
          health: healthData?.data || createMockHealth(),
          metrics: metricsData?.data?.metrics || createMockMetrics(),
          backpressure: backpressureData?.data || createMockBackpressure(),
          deadLetterQueue: dlqData?.data || createMockDLQ(),
          lastUpdated: new Date()
        };

        setData(newData);
        logWithLimit(`Fetch #${fetchId} completed successfully`);
      }

    } catch (err) {
      if (isMountedRef.current && fetchId === fetchCountRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        logWithLimit(`Fetch #${fetchId} failed: ${errorMessage}`, 'error');
      }
    } finally {
      if (isMountedRef.current && fetchId === fetchCountRef.current) {
        setLoading(false);
      }
    }
  };

  // Simple useEffect with minimal dependencies
  useEffect(() => {
    isMountedRef.current = true;
    
    // Initial fetch
    fetchData(true);
    
    // Cleanup function
    return () => {
      isMountedRef.current = false;
    };
  }, []); // Empty dependency array - no infinite loops

  // Separate useEffect for time range changes
  useEffect(() => {
    if (data) {
      fetchData(true);
    }
  }, [timeRange]); // Only depends on timeRange

  // Mock data functions
  const createMockHealth = () => ({
    status: 'healthy',
    queueDepth: 150,
    processingRate: 25,
    failureRate: 1.2,
    oldestMessageAge: 300,
    poisonMessageCount: 0,
    trend: 'stable',
    trendPercentage: 0
  });

  const createMockMetrics = () => 
    Array.from({ length: 10 }, (_, i) => ({
      timestamp: new Date(Date.now() - i * 60000).toISOString(),
      queueDepth: 150 + Math.random() * 50,
      processingRate: 20 + Math.random() * 10,
      failureRate: Math.random() * 3
    }));

  const createMockBackpressure = () => ({
    enabled: false,
    active: false,
    threshold: 1000,
    current_depth: 150
  });

  const createMockDLQ = () => ({
    total_messages: 5,
    messages: [],
    failure_reasons: { timeout: 3, error: 2 }
  });

  // Manual refresh handler
  const handleRefresh = () => {
    logWithLimit('Manual refresh triggered');
    fetchData(true);
  };

  // Render loading state
  if (loading && !data) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-bold mb-4">Queue Monitoring (Fixed Version)</h2>
        <div className="flex items-center space-x-3">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading queue data...</span>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          Fetch attempt #{fetchCountRef.current}
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !data) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-bold mb-4">Queue Monitoring (Fixed Version)</h2>
        <div className="bg-red-50 border border-red-200 rounded p-4">
          <div className="text-red-800 font-medium">Error loading queue data</div>
          <div className="text-red-600 text-sm mt-1">{error}</div>
          <button
            onClick={handleRefresh}
            className="mt-3 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">Queue Monitoring (Fixed Version)</h2>
          <p className="text-gray-600 text-sm">
            Last updated: {data?.lastUpdated.toLocaleTimeString()} | Fetch #{fetchCountRef.current}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="border border-gray-300 rounded px-3 py-1 text-sm"
          >
            <option value="5m">5 minutes</option>
            <option value="15m">15 minutes</option>
            <option value="1h">1 hour</option>
            <option value="6h">6 hours</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Status warning */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
          <div className="text-yellow-800 text-sm">
            Some data may be outdated due to API errors: {error}
          </div>
        </div>
      )}

      {/* Data display */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Queue Health */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-2">Queue Health</h3>
          <div className="text-2xl font-bold text-green-600">
            {data?.health?.status || 'Unknown'}
          </div>
          <div className="text-sm text-gray-500">
            Depth: {data?.health?.queueDepth || 0}
          </div>
        </div>

        {/* Processing Rate */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-2">Processing Rate</h3>
          <div className="text-2xl font-bold text-blue-600">
            {data?.health?.processingRate || 0}/s
          </div>
          <div className="text-sm text-gray-500">
            Failure Rate: {data?.health?.failureRate || 0}%
          </div>
        </div>

        {/* Backpressure */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-2">Backpressure</h3>
          <div className="text-2xl font-bold text-orange-600">
            {data?.backpressure?.active ? 'Active' : 'Inactive'}
          </div>
          <div className="text-sm text-gray-500">
            Threshold: {data?.backpressure?.threshold || 0}
          </div>
        </div>

        {/* Dead Letter Queue */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-2">Failed Messages</h3>
          <div className="text-2xl font-bold text-red-600">
            {data?.deadLetterQueue?.total_messages || 0}
          </div>
          <div className="text-sm text-gray-500">
            In dead letter queue
          </div>
        </div>
      </div>

      {/* Metrics Chart Placeholder */}
      <div className="bg-white border rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-4">Queue Metrics ({timeRange})</h3>
        <div className="h-48 bg-gray-50 rounded flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-500 mb-2">Metrics Data Available</div>
            <div className="text-sm text-gray-400">
              {data?.metrics?.length || 0} data points loaded
            </div>
          </div>
        </div>
      </div>

      {/* Debug Info */}
      <div className="bg-gray-50 border rounded-lg p-4 text-xs font-mono">
        <div className="font-bold mb-2">Debug Information:</div>
        <div>Component Mounted: {isMountedRef.current ? 'Yes' : 'No'}</div>
        <div>Total Fetches: {fetchCountRef.current}</div>
        <div>Loading: {loading ? 'Yes' : 'No'}</div>
        <div>Has Data: {data ? 'Yes' : 'No'}</div>
        <div>Error: {error || 'None'}</div>
      </div>
    </div>
  );
};